import React from "react";
import { ModeRendererProps } from "./types";

export function VasarelyRileyMode({ brand }: ModeRendererProps) {
  // Kinetic precision; motion from stillness
  // Wave stripes, contrast interference, vibration fields
  
  return (
    <g>
      <rect x="0" y="0" width="794" height="1123" fill={brand.white} />
      
      {/* Wave stripes - Riley's signature undulating lines */}
      {Array.from({ length: 60 }).map((_, i) => {
        const y = 80 + i * 8;
        const amplitude = 30 + Math.sin(i * 0.2) * 15;
        const frequency = 0.03;
        
        const path = Array.from({ length: 80 }).map((_, j) => {
          const x = 60 + j * 9;
          const py = y + Math.sin(x * frequency + i * 0.1) * amplitude;
          return `${j === 0 ? "M" : "L"} ${x},${py}`;
        }).join(" ");
        
        return (
          <path
            key={`wave-${i}`}
            d={path}
            fill="none"
            stroke={i % 2 === 0 ? brand.deep : brand.white}
            strokeWidth="3"
            opacity={0.9}
          />
        );
      })}
      
      {/* Contrast interference pattern */}
      {Array.from({ length: 40 }).map((_, i) => {
        const x = 80 + i * 16;
        return (
          <g key={`interference-${i}`}>
            {Array.from({ length: 30 }).map((_, j) => {
              const y = 600 + j * 16;
              const wave1 = Math.sin(i * 0.5) * 8;
              const wave2 = Math.cos(j * 0.5) * 8;
              const interference = wave1 + wave2;
              
              return (
                <rect
                  key={`cell-${j}`}
                  x={x + interference}
                  y={y}
                  width={14}
                  height={14}
                  fill={Math.abs(interference) > 6 ? brand.deep : brand.white}
                  opacity={0.9}
                />
              );
            })}
          </g>
        );
      })}
      
      {/* Vibration field - optical flicker effect */}
      {Array.from({ length: 30 }).map((_, row) =>
        Array.from({ length: 40 }).map((_, col) => {
          const x = 80 + col * 16;
          const y = 1000 + row * 4;
          const vibration = Math.sin(col * 0.3 + row * 0.7) > 0;
          
          return (
            <rect
              key={`vibration-${row}-${col}`}
              x={x}
              y={y}
              width={14}
              height={3}
              fill={vibration ? brand.deep : brand.white}
              opacity={0.95}
            />
          );
        })
      )}
      
      {/* Concentric circles with optical distortion - Vasarely influence */}
      {Array.from({ length: 20 }).map((_, i) => {
        const cx = 600;
        const cy = 300;
        const r = 20 + i * 12;
        const distortion = Math.sin(i * 0.5) * 5;
        
        return (
          <ellipse
            key={`optical-circle-${i}`}
            cx={cx + distortion}
            cy={cy}
            rx={r + Math.abs(distortion)}
            ry={r - Math.abs(distortion) * 0.5}
            fill="none"
            stroke={i % 2 === 0 ? brand.deep : brand.aqua}
            strokeWidth="2"
            opacity={0.6}
          />
        );
      })}
      
      {/* Kinetic stripes with varying width */}
      {Array.from({ length: 50 }).map((_, i) => {
        const x = 80 + i * 13;
        const width = 6 + Math.sin(i * 0.4) * 4;
        return (
          <rect
            key={`kinetic-${i}`}
            x={x}
            y="540"
            width={width}
            height="40"
            fill={i % 2 === 0 ? brand.violet : brand.white}
            opacity={0.9}
          />
        );
      })}
      
      {/* Diagonal wave interference */}
      {Array.from({ length: 40 }).map((_, i) => {
        const offset = i * 16;
        const amplitude = 15;
        
        const path = Array.from({ length: 70 }).map((_, j) => {
          const t = j / 70;
          const x = 60 + j * 10;
          const y = 60 + offset + Math.sin(t * Math.PI * 4 + i * 0.3) * amplitude;
          return `${j === 0 ? "M" : "L"} ${x},${y}`;
        }).join(" ");
        
        return (
          <path
            key={`diagonal-${i}`}
            d={path}
            fill="none"
            stroke={brand.deep}
            strokeWidth="1"
            opacity={0.3}
          />
        );
      })}
      
      {/* Motion suggestion lines */}
      {Array.from({ length: 15 }).map((_, i) => {
        const y = 950 + i * 8;
        return (
          <line
            key={`motion-${i}`}
            x1="80"
            y1={y + Math.sin(i * 0.5) * 3}
            x2="720"
            y2={y + Math.cos(i * 0.5) * 3}
            stroke={brand.violet}
            strokeWidth="2"
            opacity={0.4}
          />
        );
      })}
    </g>
  );
}
